import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import Chart from "react-apexcharts";
import { DataItem } from "../internals/interfaces";
import LoadingData from './LoadingData';
import { Box } from '@mui/material';
import { createCategoryChartConfig, createYAxisConfig, getEmissionsColors } from './shared/ChartConfig';
import dayjs from 'dayjs';

interface EmissionChartProps { 
  dateFrom: string;
  dateTo: string;
  consumptionType: number;
  aggregation: string;
  electricityData: DataItem[];
  gasData: DataItem[];
  loading: boolean;
}

const EmissionChart: React.FC<EmissionChartProps> = ({ 
  dateFrom, 
  dateTo, 
  aggregation, 
  electricityData, 
  gasData,
  loading
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [xData, setXData] = React.useState<Date[]>([]);
  const [electricityEmissionsData, setElectricityEmissionsData] = React.useState<number[]>([]);
  const [gasEmissionsData, setGasEmissionsData] = React.useState<number[]>([]);

  // Helper function to generate complete time sequence based on aggregation
  const generateTimeSequence = (from: string, to: string, agg: string): Date[] => {
    let start = dayjs(from).startOf('day');
    let end = dayjs(to).endOf('day');
    const sequence: Date[] = [];
    
    // Round down start time based on aggregation
    switch (agg) {
      case 'none':
        // Round to nearest 30 minutes
        start = start.minute() < 30 
          ? start.startOf('hour')
          : start.startOf('hour').add(30, 'minute');
        break;
      case 'hour':
        start = start.startOf('hour');
        break;
      case 'day':
        start = start.startOf('day');
        break;
      case 'week':
        // Round to start of week (Monday)
        // dayjs startOf('week') returns Sunday, so we need to adjust to Monday
        const dayOfWeek = start.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        if (dayOfWeek === 0) {
          // If Sunday, go back 6 days to get Monday
          start = start.subtract(6, 'day').startOf('day');
        } else {
          // Otherwise, go back (dayOfWeek - 1) days to get Monday
          start = start.subtract(dayOfWeek - 1, 'day').startOf('day');
        }
        break;
      case 'month':
        start = start.startOf('month');
        break;
      case 'year':
        start = start.startOf('year');
        break;
    }
    
    let current = start;
    
    while (current.isBefore(end) || current.isSame(end)) {
      sequence.push(current.toDate());
      
      // Increment based on aggregation
      switch (agg) {
        case 'none':
          current = current.add(30, 'minute');
          break;
        case 'hour':
          current = current.add(1, 'hour');
          break;
        case 'day':
          current = current.add(1, 'day');
          break;
        case 'week':
          current = current.add(1, 'week');
          break;
        case 'month':
          current = current.add(1, 'month');
          break;
        case 'year':
          current = current.add(1, 'year');
          break;
        default:
          current = current.add(1, 'hour'); // Default to 1 hour
      }
    }
    
    return sequence;
  };

  // Helper function to normalize a date to its aggregation period for comparison
  const normalizeToPeriod = (date: Date, agg: string): string => {
    const d = dayjs(date);
    switch (agg) {
      case 'none':
        return d.minute() < 30 
          ? d.startOf('hour').toISOString()
          : d.startOf('hour').add(30, 'minute').toISOString();
      case 'hour':
        return d.startOf('hour').toISOString();
      case 'day':
        return d.startOf('day').toISOString();
      case 'week':
        const dayOfWeek = d.day();
        if (dayOfWeek === 0) {
          return d.subtract(6, 'day').startOf('day').toISOString();
        } else {
          return d.subtract(dayOfWeek - 1, 'day').startOf('day').toISOString();
        }
      case 'month':
        return d.startOf('month').toISOString();
      case 'year':
        return d.startOf('year').toISOString();
      default:
        return d.toISOString();
    }
  };

  React.useEffect(() => {
    if (electricityData.length > 0 || gasData.length > 0) {
      // Generate complete time sequence
      const completeTimeSequence = generateTimeSequence(dateFrom, dateTo, aggregation);
      
      // Create maps of data by normalized time period
      const electricityDataMap = new Map<string, DataItem>();
      electricityData.forEach(item => {
        const itemTime = new Date(item.reading_time);
        const normalizedKey = normalizeToPeriod(itemTime, aggregation);
        electricityDataMap.set(normalizedKey, item);
      });
      
      const gasDataMap = new Map<string, DataItem>();
      gasData.forEach(item => {
        const itemTime = new Date(item.reading_time);
        const normalizedKey = normalizeToPeriod(itemTime, aggregation);
        gasDataMap.set(normalizedKey, item);
      });
      
      // Build arrays aligned to the time sequence
      const xDataArray: Date[] = [];
      const alignedElectricityData: number[] = [];
      const alignedGasData: number[] = [];
      
      completeTimeSequence.forEach(timePoint => {
        const normalizedKey = normalizeToPeriod(timePoint, aggregation);
        
        xDataArray.push(timePoint);
        
        // Get electricity data for this time point
        const electricityItem = electricityDataMap.get(normalizedKey);
        alignedElectricityData.push(electricityItem?.gCO_2 || 0);
        
        // Get gas data for this time point
        const gasItem = gasDataMap.get(normalizedKey);
        alignedGasData.push(gasItem?.gCO_2 || 0);
      });
      
      setXData(xDataArray);
      setElectricityEmissionsData(alignedElectricityData);
      setGasEmissionsData(alignedGasData);
    } else {
      setXData([]);
      setElectricityEmissionsData([]);
      setGasEmissionsData([]);
    }
  }, [electricityData, gasData, dateFrom, dateTo, aggregation]);

  const getTotal = (emissions: number[], factor: number = 1, dp: number = 2) => {
    let sum = 0
    emissions.forEach((num: number) => {
      sum += num;
    });
    return (sum * factor).toLocaleString(undefined, {maximumFractionDigits:dp});
  }

  const formatDayAxis = (value: string) => {
    const date = new Date(value);
    const day = `${date.getDate()}`;
    const month = date.toLocaleString('default', { month: 'short' });
    switch (aggregation) {
      case 'minute':
          return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      case 'hour':
          return date.toLocaleTimeString('en-GB', { minute: '2-digit', hour: '2-digit' });
      case 'day':          
          return `${day} ${month}`;
      case 'week':
          return `${day} ${month}`;
      case 'month':
          return date.toLocaleString('default', { month: 'short' });
      case 'year':
          return date.getFullYear().toString();
      default:
        return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    };
  };

  const getSuffix = (day: number) => {
    return (day % 10 === 1 && day !== 11) ? 'st' : 
      (day % 10 === 2 && day !== 12) ? 'nd' : 
      (day % 10 === 3 && day !== 13) ? 'rd' : 'th';
  };

  const formatDayTitle = (from: string, to: string) => {
    const dateFrom = new Date(from);
    const dateTo = new Date(to);
    const dayFrom = `${dateFrom.getDate()}`;
    const suffix = getSuffix(dateFrom.getDate())
    const monthFrom = isMobile 
      ? dateFrom.toLocaleString('default', { month: 'short' })
      : dateFrom.toLocaleString('default', { month: 'long' });
    const yearFrom = `${dateFrom.getFullYear()}`;
    const dayTo = `${dateTo.getDate()}`;        
    const suffixTo = getSuffix(dateTo.getDate())
    const monthTo = isMobile 
      ? dateTo.toLocaleString('default', { month: 'short' })
      : dateTo.toLocaleString('default', { month: 'long' });
    const yearTo = `${dateTo.getFullYear()}`;

    switch (aggregation) {
      case 'none':
        return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
      case 'hour':
        return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
      case 'month':
        if (monthFrom === monthTo) {
          return `${monthFrom} ${yearTo}`;
        } else {          
          if (yearFrom === yearTo) {
            return `${monthFrom} - ${monthTo} ${yearTo}`;
          } else {
            return `${monthFrom} ${yearFrom} - ${monthTo} ${yearTo}`;
          }
        }
      case 'year':
        if (yearFrom === yearTo) {
          return `${yearTo}`;
        } else {          
          return `${yearFrom} - ${yearTo}`;
        }
      default:        
        if (yearFrom === yearTo) {
          if (dayFrom === dayTo) {
            return `${dayFrom}${suffix} ${monthFrom} ${yearFrom}`;
          }
          return `${dayFrom}${suffix} ${monthFrom} - ${dayTo}${suffixTo} ${monthTo} ${yearTo}`;
        } else {
          return `${dayFrom}${suffix} ${monthFrom} ${yearFrom} - ${dayTo}${suffixTo} ${monthTo} ${yearTo}`;
        }
    }
  };

  // Theme-aware colors for dark mode compatibility
  const chartColors = getEmissionsColors(theme);
  
  const state = {
    options: {
      ...createCategoryChartConfig({
        theme,
        chartType: 'bar',
        height: 350,
        categories: xData,
        tickAmount: 6,
        tickPlacement: 'between',
        labelFormatter: formatDayAxis,
        chartId: 'emissions',
        stacked: true,
        isMobile,
        foreColor: chartColors.text
      }),
      // Show a data label on consumption bars when there is no data (null) to indicate "No data".
      dataLabels: {
          enabled: true,
        offsetY: -53,
        formatter: function(val: any, opts: any) {
          // Only show "No data" for the consumption series (seriesIndex 0) when value is null/undefined/NaN
          if (!loading && electricityData.some(x => x.consumption !== null) && gasData.some(x => x.consumption !== null)) {
            if (opts && opts.seriesIndex === 0 && (val === null || val === undefined || Number.isNaN(val))) {
              return 'No data';
            }
          }
          // Do not show labels for other values/series
          return '';
        },
        style: {
          colors: [theme.palette.text.secondary],
          fontSize: isMobile ? '10px' : '14px',
          fontWeight: 600
        },
        background: {
          enabled: false
        }
      },
      yaxis: {
        ...createYAxisConfig(theme, aggregation === 'none' || aggregation === 'hour' ? 'CO₂ emissions (g)' : 'CO₂ emissions (kg)', {
          decimalsInFloat: aggregation === 'none' ? 0 : 2
        }),
        title: {
          ...createYAxisConfig(theme, aggregation === 'none' || aggregation === 'hour' ? 'CO₂ emissions (g)' : 'CO₂ emissions (kg)', {
            decimalsInFloat: aggregation === 'none' ? 0 : 2
          }).title,
          style: {
            fontSize: isMobile ? '13px' : '16px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: isMobile ? 500 : 700,
            color: theme.palette.text.primary
          }
        }
      },
      colors: [
        ...(gasData.length > 0 ? [chartColors.gas] : []),
        ...(electricityData.length > 0 ? [chartColors.emissions] : [])
      ],
      ...(isMobile && {
        xaxis: {
          ...createCategoryChartConfig({
            theme,
            chartType: 'bar',
            height: 350,
            categories: xData,
            tickAmount: 6,
            tickPlacement: 'between',
            labelFormatter: formatDayAxis,
            chartId: 'emissions',
            isMobile,
            foreColor: chartColors.text,
            showLegend: true
          }).xaxis,
          tickAmount: 3
        }
      }),
      tooltip: {
        enabled: true,
        theme: theme.palette.mode,
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif'
        },
        y: {
          formatter: function(value: number, { seriesIndex }: { seriesIndex: number }) {
            // For CO2 emissions series (electricity and gas), show 2 decimal places
            if (aggregation === 'none' || aggregation === 'hour') {
              return value.toFixed(2) + ' g';
            } else {
              return value.toFixed(2) + ' kg';
            }
          }
        }
      }
    },
    series: [
      ...(gasData.length > 0 ? [{
        name: aggregation === 'none' || aggregation === 'hour' ? 'Gas CO₂ emissions (g)' : 'Gas CO₂ emissions (kg)',
        type: 'bar' as const,
        data: gasEmissionsData
      }] : []),
      ...(electricityData.length > 0 ? [{
        name: aggregation === 'none' || aggregation === 'hour' ? 'Electricity CO₂ emissions (g)' : 'Electricity CO₂ emissions (kg)',
        type: 'bar' as const,
        data: electricityEmissionsData
      }] : [])
    ]
  };

  return (
    <Card sx={{ width: '100%', position: 'relative', backgroundColor: theme.palette.background.paper, px: { xs: 1, md: 0 } }} data-chart-id="emissions-1">
      <CardContent>
        <LoadingData open={loading} />
        <Box sx={{ mb: 1 }}>
          <Typography component="h2" variant="h4">
            Emissions
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            {formatDayTitle(dateFrom, dateTo)}
          </Typography>
          {((electricityData.length > 0 && electricityData.some(item => item.consumption !== null))
            || (gasData.length > 0 && gasData.some(item => item.consumption !== null))) && (
            <Stack
              direction="row"
              spacing={{ xs: 3, md: 4 }}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 2 }}
            >
              {electricityData.length > 0 && (
                <Box>
                  <Typography component="h2" variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', fontSize: { xs: '13px', sm: '16px' } }}>
                    Total CO₂ Emitted
                  </Typography>
                  <Typography variant="h2" component="p" sx={{ fontSize: { xs: '22px', sm: '32px' } }}>
                    {aggregation === 'none' || aggregation === 'hour'
                      ? getTotal(electricityEmissionsData) + ' g'
                      : getTotal(electricityEmissionsData) + ' kg'}
                  </Typography>
                </Box>
              )}
              {gasData.length > 0 && (
                <Box>
                  <Typography component="h2" variant="subtitle2" gutterBottom sx={{ color: 'text.secondary', fontSize: { xs: '13px', sm: '16px' } }}>
                    Gas CO₂ Emissions
                  </Typography>
                  <Typography variant="h4" component="p" sx={{ fontSize: { xs: '22px', sm: '32px' } }}>
                    {aggregation === 'none' || aggregation === 'hour'
                      ? getTotal(gasEmissionsData) + ' g'
                      : getTotal(gasEmissionsData) + ' kg'}
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </Box>
        <Box sx={{ position: 'relative', ml: { xs: '-5px', md: 0 }, mr: { xs: '-20px', md: 0 }, mb: { xs: '-10px', md: 0 } }}>
          <Chart 
            options={state.options}
            series={state.series}
            type="bar"
            height={isMobile ? Math.min(250, window.innerWidth * 0.8) : 350}
          />
          
          {/* No data overlay */}
          {!loading && (electricityData.length === 0 || !electricityData.some(x => x.consumption !== null)) 
             && (gasData.length === 0 || !gasData.some(x => x.consumption !== null)) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: theme.palette.surface,
                zIndex: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                No emissions data
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmissionChart;
